import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
    Plus,
    TrendingUp,
    Sparkles,
    Calendar,
    AlertTriangle,
    Activity,
    Eye,
    EyeOff,
    Trash2,
    Save
} from "lucide-react";

const CATEGORIES = [
    { id: "football", name: "Football" },
    { id: "basketball", name: "Basketball" },
    { id: "athletics", name: "Athletics" },
    { id: "rugby", name: "Rugby" },
    { id: "cricket", name: "Cricket" },
    { id: "tennis", name: "Tennis" },
    { id: "boxing", name: "Boxing" },
];

const PredictionsCenter = () => {
    const { token } = useAuth();
    const [predictions, setPredictions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [formData, setFormData] = useState({
        match_title: "",
        home_team: "",
        away_team: "",
        sport: "football",
        match_date: "",
        prediction_text: "",
        prediction_text_sw: "",
        confidence_score: 70,
        key_factors: "",
        home_injuries: "",
        away_injuries: "",
        recent_form_home: "",
        recent_form_away: "",
        head_to_head: ""
    });

    useEffect(() => {
        fetchPredictions();
    }, []);

    const fetchPredictions = async () => {
        try {
            const response = await axios.get(`${API}/predictions?limit=50&upcoming=false&include_hidden=true`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPredictions(response.data);
        } catch (error) {
            toast.error("Failed to fetch predictions");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this prediction permanently?")) return;
        try {
            await axios.delete(`${API}/predictions/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Prediction deleted");
            fetchPredictions();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const handleToggleVisibility = async (id) => {
        try {
            await axios.patch(`${API}/predictions/${id}/toggle-visibility`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Visibility updated");
            fetchPredictions();
        } catch {
            toast.error("Failed to toggle");
        }
    };

    const handleCreate = () => {
        setFormData({
            match_title: "",
            home_team: "",
            away_team: "",
            sport: "football",
            match_date: "",
            prediction_text: "",
            prediction_text_sw: "",
            confidence_score: 70,
            key_factors: "",
            home_injuries: "",
            away_injuries: "",
            recent_form_home: "",
            recent_form_away: "",
            head_to_head: ""
        });
        setIsDialogOpen(true);
    };

    const handleGenerateAI = async () => {
        if (!formData.home_team || !formData.away_team) {
            toast.error("Please enter both teams first");
            return;
        }
        setGenerating(true);
        try {
            const response = await axios.post(
                `${API}/ai/generate-prediction?home_team=${encodeURIComponent(formData.home_team)}&away_team=${encodeURIComponent(formData.away_team)}&sport=${formData.sport}&match_date=${formData.match_date || new Date().toISOString()}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const data = response.data;
            if (data.generated && data.prediction_data) {
                try {
                    const parsed = typeof data.prediction_data === "string" 
                        ? JSON.parse(data.prediction_data) 
                        : data.prediction_data;
                    
                    setFormData(prev => ({
                        ...prev,
                        prediction_text: parsed.predicted_outcome || parsed.prediction || "",
                        confidence_score: parsed.confidence_score || 65,
                        key_factors: Array.isArray(parsed.key_factors) ? parsed.key_factors.join(", ") : "",
                        recent_form_home: parsed.recent_form_home || "",
                        recent_form_away: parsed.recent_form_away || "",
                        home_injuries: Array.isArray(parsed.home_injuries) ? parsed.home_injuries.join(", ") : 
                                       Array.isArray(parsed.injuries) ? parsed.injuries.join(", ") : "",
                    }));
                    toast.success("AI prediction generated!");
                } catch {
                    setFormData(prev => ({
                        ...prev,
                        prediction_text: String(data.prediction_data)
                    }));
                }
            } else {
                // Use mock data
                setFormData(prev => ({
                    ...prev,
                    prediction_text: data.prediction_data?.predicted_outcome || `${formData.home_team} vs ${formData.away_team} - Prediction pending`,
                    confidence_score: data.prediction_data?.confidence_score || 65,
                    key_factors: data.prediction_data?.key_factors?.join(", ") || "Home advantage, Recent form"
                }));
                toast.info("Mock prediction generated (AI service unavailable)");
            }
        } catch (error) {
            toast.error("Failed to generate AI prediction");
        } finally {
            setGenerating(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                match_title: formData.match_title || `${formData.home_team} vs ${formData.away_team}`,
                home_team: formData.home_team,
                away_team: formData.away_team,
                sport: formData.sport,
                match_date: formData.match_date || new Date().toISOString(),
                prediction_text: formData.prediction_text,
                prediction_text_sw: formData.prediction_text_sw || formData.prediction_text,
                confidence_score: formData.confidence_score,
                key_factors: formData.key_factors.split(",").map(f => f.trim()).filter(Boolean),
                home_injuries: formData.home_injuries.split(",").map(i => i.trim()).filter(Boolean),
                away_injuries: formData.away_injuries.split(",").map(i => i.trim()).filter(Boolean),
                recent_form_home: formData.recent_form_home,
                recent_form_away: formData.recent_form_away,
                head_to_head: formData.head_to_head
            };

            await axios.post(`${API}/predictions`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Prediction created successfully");
            setIsDialogOpen(false);
            fetchPredictions();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to create prediction");
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const getConfidenceColor = (score) => {
        if (score >= 75) return "text-electric";
        if (score >= 50) return "text-solar";
        return "text-red-400";
    };

    return (
        <AdminLayout title="Predictions Center">
            <div className="space-y-6" data-testid="predictions-center">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
                                <TrendingUp className="h-5 w-5 text-electric" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Total Predictions</p>
                                <p className="text-2xl font-heading font-bold text-white">{predictions.length}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-electric/10 flex items-center justify-center">
                                <Activity className="h-5 w-5 text-electric" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Avg Confidence</p>
                                <p className="text-2xl font-heading font-bold text-electric">
                                    {predictions.length > 0 
                                        ? Math.round(predictions.reduce((acc, p) => acc + p.confidence_score, 0) / predictions.length)
                                        : 0}%
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-paper border-border">
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                <Eye className="h-5 w-5 text-blue-400" />
                            </div>
                            <div>
                                <p className="text-white/50 text-sm">Total Views</p>
                                <p className="text-2xl font-heading font-bold text-blue-400">
                                    {predictions.reduce((acc, p) => acc + (p.views || 0), 0)}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Toolbar */}
                <div className="flex justify-between items-center">
                    <h2 className="text-white font-heading uppercase">All Predictions</h2>
                    <Button onClick={handleCreate} className="btn-primary" data-testid="create-prediction-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Prediction
                    </Button>
                </div>

                {/* Predictions List */}
                {loading ? (
                    <div className="grid gap-4">
                        {[...Array(3)].map((_, i) => (
                            <Card key={i} className="bg-paper border-border animate-pulse">
                                <CardContent className="p-6">
                                    <div className="h-5 bg-muted rounded w-1/3 mb-4" />
                                    <div className="h-4 bg-muted rounded w-2/3 mb-2" />
                                    <div className="h-4 bg-muted rounded w-1/2" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : predictions.length > 0 ? (
                    <div className="grid gap-4">
                        {predictions.map((pred, idx) => (
                            <Card 
                                key={pred.id} 
                                className="bg-paper border-border"
                                data-testid={`prediction-item-${idx}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <Badge className="bg-electric/10 text-electric border-electric/30">
                                                    {CATEGORIES.find(c => c.id === pred.sport)?.name || pred.sport}
                                                </Badge>
                                                <span className="text-white/40 text-sm flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatDate(pred.match_date)}
                                                </span>
                                                <span className="text-white/40 text-sm flex items-center gap-1">
                                                    <Eye className="h-3 w-3" />
                                                    {pred.views} views
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-heading font-bold text-white uppercase mb-2">
                                                {pred.home_team} vs {pred.away_team}
                                            </h3>
                                            <p className="text-white/60 text-sm mb-4">{pred.prediction_text}</p>

                                            {/* Key Factors */}
                                            {pred.key_factors && pred.key_factors.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {pred.key_factors.map((factor, i) => (
                                                        <span key={i} className="text-xs bg-subtle px-2 py-1 rounded text-white/50">
                                                            {factor}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Injuries */}
                                            {(pred.home_injuries?.length > 0 || pred.away_injuries?.length > 0) && (
                                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    <span>
                                                        {pred.home_injuries?.length || 0} + {pred.away_injuries?.length || 0} injuries
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="text-center shrink-0">
                                            <div className={`text-4xl font-heading font-black ${getConfidenceColor(pred.confidence_score)}`}>
                                                {pred.confidence_score}%
                                            </div>
                                            <p className="text-white/40 text-xs uppercase mt-1">Confidence</p>
                                            <div className="flex items-center gap-1 mt-3 justify-center">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleToggleVisibility(pred.id)}
                                                    className={pred.is_hidden ? "text-yellow-400 hover:text-yellow-300" : "text-white/60 hover:text-white"}
                                                    title={pred.is_hidden ? "Show on public site" : "Hide from public site"}
                                                    data-testid={`toggle-prediction-${idx}`}
                                                >
                                                    {pred.is_hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(pred.id)}
                                                    className="text-red-400 hover:text-red-300"
                                                    title="Delete"
                                                    data-testid={`delete-prediction-${idx}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            {pred.is_hidden && (
                                                <Badge className="mt-2 bg-yellow-500/10 text-yellow-400 border-yellow-500/30 text-[10px]">
                                                    Hidden
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-paper border-border">
                        <CardContent className="p-12 text-center">
                            <TrendingUp className="h-16 w-16 text-white/20 mx-auto mb-4" />
                            <h3 className="text-white font-heading text-xl uppercase mb-2">No Predictions Yet</h3>
                            <p className="text-white/50 mb-4">Create AI-powered match predictions</p>
                            <Button onClick={handleCreate} className="btn-primary">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Prediction
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Create Dialog */}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="bg-paper border-border max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white font-heading uppercase">
                                Create Prediction
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4" data-testid="prediction-form">
                            {/* Teams */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white">Home Team</Label>
                                    <Input
                                        value={formData.home_team}
                                        onChange={(e) => setFormData({...formData, home_team: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        placeholder="e.g. Manchester United"
                                        data-testid="home-team-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Away Team</Label>
                                    <Input
                                        value={formData.away_team}
                                        onChange={(e) => setFormData({...formData, away_team: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        placeholder="e.g. Liverpool"
                                        data-testid="away-team-input"
                                    />
                                </div>
                            </div>

                            {/* Sport & Date */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white">Sport</Label>
                                    <Select 
                                        value={formData.sport} 
                                        onValueChange={(v) => setFormData({...formData, sport: v})}
                                    >
                                        <SelectTrigger className="bg-subtle border-border text-white" data-testid="sport-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(cat => (
                                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-white">Match Date</Label>
                                    <Input
                                        type="datetime-local"
                                        value={formData.match_date}
                                        onChange={(e) => setFormData({...formData, match_date: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="match-date-input"
                                    />
                                </div>
                            </div>

                            {/* AI Generate Button */}
                            <div className="flex justify-center">
                                <Button
                                    type="button"
                                    onClick={handleGenerateAI}
                                    disabled={generating || !formData.home_team || !formData.away_team}
                                    className="bg-gradient-to-r from-electric to-solar text-black font-bold"
                                    data-testid="generate-ai-btn"
                                >
                                    {generating ? (
                                        <span className="animate-pulse">Generating...</span>
                                    ) : (
                                        <>
                                            <Sparkles className="h-4 w-4 mr-2" />
                                            Generate AI Prediction
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* Prediction Text */}
                            <div>
                                <Label className="text-white">Prediction (English)</Label>
                                <Textarea
                                    value={formData.prediction_text}
                                    onChange={(e) => setFormData({...formData, prediction_text: e.target.value})}
                                    className="bg-subtle border-border text-white"
                                    placeholder="Your prediction analysis..."
                                    data-testid="prediction-text-input"
                                />
                            </div>
                            <div>
                                <Label className="text-white">Prediction (Swahili)</Label>
                                <Textarea
                                    value={formData.prediction_text_sw}
                                    onChange={(e) => setFormData({...formData, prediction_text_sw: e.target.value})}
                                    className="bg-subtle border-border text-white"
                                    placeholder="Utabiri kwa Kiswahili..."
                                    data-testid="prediction-text-sw-input"
                                />
                            </div>

                            {/* Confidence */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="text-white">Confidence Score</Label>
                                    <span className={`font-bold ${getConfidenceColor(formData.confidence_score)}`}>
                                        {formData.confidence_score}%
                                    </span>
                                </div>
                                <Slider
                                    value={[formData.confidence_score]}
                                    onValueChange={(v) => setFormData({...formData, confidence_score: v[0]})}
                                    max={100}
                                    step={1}
                                    className="py-2"
                                    data-testid="confidence-slider"
                                />
                            </div>

                            {/* Key Factors */}
                            <div>
                                <Label className="text-white">Key Factors (comma separated)</Label>
                                <Input
                                    value={formData.key_factors}
                                    onChange={(e) => setFormData({...formData, key_factors: e.target.value})}
                                    className="bg-subtle border-border text-white"
                                    placeholder="e.g. Home advantage, Recent form, Head to head"
                                    data-testid="key-factors-input"
                                />
                            </div>

                            {/* Form */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white">Home Form (e.g. W-W-D-L-W)</Label>
                                    <Input
                                        value={formData.recent_form_home}
                                        onChange={(e) => setFormData({...formData, recent_form_home: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="form-home-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Away Form</Label>
                                    <Input
                                        value={formData.recent_form_away}
                                        onChange={(e) => setFormData({...formData, recent_form_away: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="form-away-input"
                                    />
                                </div>
                            </div>

                            {/* Injuries */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-white">Home Injuries (comma separated)</Label>
                                    <Input
                                        value={formData.home_injuries}
                                        onChange={(e) => setFormData({...formData, home_injuries: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        placeholder="e.g. Player A, Player B"
                                        data-testid="injuries-home-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Away Injuries</Label>
                                    <Input
                                        value={formData.away_injuries}
                                        onChange={(e) => setFormData({...formData, away_injuries: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="injuries-away-input"
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    variant="ghost"
                                    onClick={() => setIsDialogOpen(false)}
                                    className="text-white/60"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSave}
                                    disabled={saving || !formData.home_team || !formData.away_team || !formData.prediction_text}
                                    className="btn-primary"
                                    data-testid="save-prediction-btn"
                                >
                                    {saving ? (
                                        <span className="animate-pulse">Saving...</span>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4 mr-2" />
                                            Save Prediction
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default PredictionsCenter;
